import React, { useContext, useEffect, useState } from 'react'
import { GoogleAuthProvider, OAuthProvider, signInWithPopup, signInWithRedirect, getRedirectResult } from 'firebase/auth'
import { FirebaseContext } from '@/cinematics/providers/FirebaseProvider'
import { useAuth } from '@/hooks/auth/useAuth'
import { LoadingOverlay } from '@/components/Loading/Overlay'
import { NextImage } from '@/components/NextImage'
import { toSsoAuthError, SSO_ERROR_CODES } from '@/actors/AuthActor/utils/toSsoAuthError'
import * as Sentry from '@sentry/nextjs'
import settings from '@/settings/public'

import classNames from 'classnames'
import classes from './styles.module.scss'
interface FirebaseAuthProps {
  methods?: string[]
  /**
   * From the scene's `options.denyRegister`. Travels with the idToken so the
   * backend refuses to create an account for an unknown federated identity.
   */
  denyRegister?: boolean
  /**
   * Every failure that is not the visitor dismissing the popup. Without it the
   * whole federated path had no way to say anything: the `catch` below ended in
   * no branch at all, so a backend rejection returned the visitor to an
   * unchanged screen — the PRO-736 repro, and what failed Amazon's review.
   */
  onError?: (error: unknown) => void
}

// Pure helpers — no component state, hoisted to module scope so they can be
// referenced from the effect without a temporal-dead-zone violation.
const getFirebaseErrorCode = (error: unknown): string | undefined => {
  if (typeof error !== 'object' || !error) {
    return undefined
  }

  const firebaseError = error as { code?: string }
  return firebaseError.code
}

const isDismissedPopupError = (error: unknown): boolean => {
  const errorCode = getFirebaseErrorCode(error)
  return errorCode === 'auth/cancelled-popup-request' || errorCode === 'auth/popup-closed-by-user'
}

const isBlockedPopupError = (error: unknown): boolean => {
  return getFirebaseErrorCode(error) === 'auth/popup-blocked'
}

export const FirebaseAuth: React.FC<FirebaseAuthProps> = (props) => {
  const { methods, denyRegister = false, onError } = props
  const { firebaseAuth } = useContext(FirebaseContext)
  const { firebaseSsoMutation } = useAuth()
  const { isPending } = firebaseSsoMutation
  const [isPopupPending, setIsPopupPending] = useState(false)
  const isLoading = isPending || isPopupPending

  // Omitted entirely unless the scene asks for it, so every other route keeps
  // sending exactly the payload it sends today.
  const buildFirebasePayload = (idToken: string) => (
    denyRegister ? { idToken, denyRegister: true } : { idToken }
  )

  /**
   * The single exit for a real failure: telemetry, then the caller.
   *
   * Sentry gets a stable message for anything that is not already an `Error`
   * (the backend rejection arrives as a plain `{ code, userMessage, extra }`
   * envelope, which would otherwise group as "Non-Error exception captured"),
   * with the code on a tag so the backend's 42215 and the SDK's `auth/…` stay
   * separable without fragmenting the issue per provider or per route.
   */
  const reportFailure = (error: unknown, provider: string) => {
    // Same split the checkout classifier makes: an expected outcome is not a
    // fault. A visitor whose Google identity simply has no account here is the
    // everyday case on a sign-in-only scene, and at review-traffic volume it
    // would drown the real faults it sits next to.
    const isExpectedOutcome = toSsoAuthError(error).code === SSO_ERROR_CODES.ACCOUNT_NOT_FOUND

    Sentry.captureException(
      error instanceof Error ? error : new Error('Federated sign-in failed'),
      {
        level: isExpectedOutcome ? 'warning' : 'error',
        tags: {
          ssoProvider: provider,
          authScene: window.location.pathname,
          ssoErrorCode: String((error as { code?: unknown })?.code ?? 'unknown'),
        },
        extra: { ssoError: error },
      }
    )

    onError?.(error)
  }

  useEffect(() => {
    const handleRedirectResult = async () => {
      let result

      try {
        result = await getRedirectResult(firebaseAuth)
      } catch (error) {
        if (isDismissedPopupError(error)) {
          return
        }
        // IndexedDB errors on iOS Safari (Private Browsing, low memory, etc.) — silently ignore
        console.warn('[FirebaseAuth] getRedirectResult error (may be IndexedDB on iOS Safari):', error)
        return
      }

      if (!result) {
        return
      }

      // Split from the call above on purpose: the swallow up there is scoped to
      // Firebase's own storage faults. Everything from here on is the same
      // exchange the popup makes — and it rejects for the same reasons, so it
      // must surface the same way. The redirect flow is not a rare path: it is
      // where a blocked popup lands.
      try {
        const idToken = await result.user.getIdToken(true)
        await firebaseSsoMutation.mutateAsync({ firebase: buildFirebasePayload(idToken) })
      } catch (error) {
        reportFailure(error, result.providerId || 'unknown')
      }
    }

    handleRedirectResult()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseAuth])

  const handleSignIn = async (provider: string) => {
    if (isLoading) {
      return
    }

    setIsPopupPending(true)
    let providerInstance = null

    if (provider === 'google') {
      providerInstance = new GoogleAuthProvider()
      providerInstance.setCustomParameters({ prompt: 'select_account' })
    }

    if (provider === 'apple') {
      providerInstance = new OAuthProvider('apple.com')
      providerInstance.addScope('email')
    }

    if (!providerInstance) return

    try {
      const result = await signInWithPopup(firebaseAuth, providerInstance)
      const idToken = await result.user.getIdToken(true)
      await firebaseSsoMutation.mutateAsync({ firebase: buildFirebasePayload(idToken) })

    } catch (error) {
      if (isDismissedPopupError(error)) {
        return
      }

      if (isBlockedPopupError(error)) {
        try {
          await signInWithRedirect(firebaseAuth, providerInstance)
        } catch (redirectError) {
          // IndexedDB errors on iOS Safari (Private Browsing, low memory, etc.)
          // signInWithRedirect internally uses IndexedDB to store redirect state.
          // Swallow these errors to prevent unhandled rejections bubbling to Sentry.
          console.warn('[FirebaseAuth] signInWithRedirect error (may be IndexedDB on iOS Safari):', redirectError)
        }
        return
      }

      reportFailure(error, provider)
    } finally {
      setIsPopupPending(false)
    }
  }

  return (
    <>
      <LoadingOverlay isLoading={isLoading} />
      <div className={classes.firebase}>
        {methods?.map((method: string) => (
          <div
            key={method}
            onClick={isLoading ? undefined : () => handleSignIn(method)}
            data-testid={`auth-form-firebase-${method}`}
            className={classNames({
              [classes.default]: true,
              [classes.apple]: method === 'apple',
              [classes.google]: method === 'google',
              [classes.disabled]: isLoading,
            })}
          >
            <div className={classes.content}>
              <NextImage
                src={settings.images?.storeLogo?.[method as keyof typeof settings.images.storeLogo] || ''}
                alt={method}
                width={24}
                height={24}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  )
}
